import { useMemo, useState } from "react";

import { CheckIcon, MailIcon, SendIcon, XIcon } from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Message } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import type {
  CompanyMessage,
  InnointelCompany,
} from "components/innointel/types";
import { UserChip } from "components/lib/Chips";

import { ContactCompaniesModal } from "../../UI/ContactCompaniesModal";
import { InfoBoxWidget, InfoBoxWidgetTitleArea } from "../Styles";

const MessageView = styled.div`
  .subject {
    margin: 0 0 12px;
    font-size: 18px;
    font-weight: 600;
    color: #111418;
  }

  dl {
    display: grid;
    grid-template-columns: max-content 1fr;
    align-items: center;
    gap: 6px 16px;
    margin: 0 0 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid #eef0f2;
    font-size: 13px;
  }

  dt {
    color: #5b6470;
  }

  dd {
    margin: 0;
    min-width: 0;
    color: #1f2328;
    overflow-wrap: anywhere;
  }

  .status {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .pill {
    padding: 1px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    background: #f1f3f5;
    color: #5b6470;
  }

  .pill.good {
    background: #e6f4ea;
    color: #1e7b34;
  }

  .pill.bad {
    background: #fdecea;
    color: #b42318;
  }

  .body {
    padding: 14px 16px;
    border-radius: 8px;
    background: #f6f8fa;
    font-size: 14px;
    line-height: 1.55;
    color: #1f2328;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
`;

type EmailLog = NonNullable<CompanyMessage["emailLogs"]>[number];

/** The furthest the email got, from the email provider's events. */
const deliveryStatus = (log: EmailLog) =>
  log.events?.bounced
    ? { label: "Bounced", tone: "bad" }
    : log.events?.open
      ? { label: "Opened", tone: "good" }
      : log.events?.delivered
        ? { label: "Delivered", tone: "good" }
        : log.sent
          ? { label: "Sent", tone: "" }
          : { label: "Not sent yet", tone: "" };

const DeliveryPill = ({ log }: { log: EmailLog }) => {
  const status = deliveryStatus(log);
  return <span className={`pill ${status.tone}`}>{status.label}</span>;
};

type CompanyMessagesProps = {
  messages: CompanyMessage[];
  /** Offers writing a new message to the company */
  company?: InnointelCompany;
};

const CompanyMessages = ({ messages, company }: CompanyMessagesProps) => {
  const [selectedMessage, setSelectedMessage] = useState<CompanyMessage | null>(
    null,
  );
  const [isWriting, setIsWriting] = useState(false);
  const allMessagesWithLogs = useMemo(
    () => messages.filter((message) => !!message.emailLogs?.length),
    [messages],
  );

  const user = useCurrentUser();
  const organisationId = user?.ownerOrganisation?._id;
  const canListUsers =
    user && util.hasPermission(user, "org.listUsers", organisationId);

  return (
    <>
      <Modal
        open={!!selectedMessage}
        onClose={() => setSelectedMessage(null)}
        closeOnDimmerClick
      >
        <ModalHeader>
          {company?.name ? `Message to ${company.name}` : "Message"}
        </ModalHeader>
        {selectedMessage ? (
          <ModalContent>
            <MessageView>
              <h3 className="subject">
                {selectedMessage.subject || "(No subject)"}
              </h3>
              <dl>
                {canListUsers && selectedMessage.ownerUser ? (
                  <>
                    <dt>From</dt>
                    <dd>
                      <UserChip user={selectedMessage.ownerUser} />
                    </dd>
                  </>
                ) : null}
                {selectedMessage.email ? (
                  <>
                    <dt>To</dt>
                    <dd>{selectedMessage.email}</dd>
                  </>
                ) : null}
                {selectedMessage.replyTo ? (
                  <>
                    <dt>Reply-to</dt>
                    <dd>{selectedMessage.replyTo}</dd>
                  </>
                ) : null}
                <dt>Sent</dt>
                <dd>
                  {formatDate(selectedMessage.createdAt, "MMM d, yyyy, HH:mm")}
                </dd>
                {selectedMessage.emailLogs?.[0] ? (
                  <>
                    <dt>Status</dt>
                    <dd className="status">
                      <DeliveryPill log={selectedMessage.emailLogs[0]} />
                    </dd>
                  </>
                ) : null}
              </dl>
              <div className="body">{selectedMessage.message}</div>
            </MessageView>
          </ModalContent>
        ) : null}
        <ModalActions>
          <Button variant="outline" onClick={() => setSelectedMessage(null)}>
            Close
          </Button>
        </ModalActions>
      </Modal>
      {isWriting && company ? (
        <ContactCompaniesModal
          companies={[
            {
              ...company,
              contact: {
                count: messages.length,
                lastContactedAt: messages[0]?.createdAt ?? null,
              },
            },
          ]}
          onClose={() => setIsWriting(false)}
        />
      ) : null}
      <InfoBoxWidget $noBox>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <InfoBoxWidgetTitleArea>
            <Icon icon={MailIcon} />
            <div>
              <H3>Messages</H3>
              <span>{messages.length} total</span>
            </div>
          </InfoBoxWidgetTitleArea>
          {company ? (
            <Button variant="outline" onClick={() => setIsWriting(true)}>
              <Icon icon={SendIcon} />
              New message
            </Button>
          ) : null}
        </div>
        <p>
          These are communications sent by you or someone working in your
          organisation to this company.
        </p>
        <div style={{ overflowX: "auto", maxWidth: "100%" }}>
          {messages.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Subject</TableHeaderCell>
                  {canListUsers ? (
                    <TableHeaderCell>From</TableHeaderCell>
                  ) : null}
                  <TableHeaderCell>Reply-To address</TableHeaderCell>
                  <TableHeaderCell>Date</TableHeaderCell>
                  {allMessagesWithLogs.length > 0 ? (
                    <>
                      <TableHeaderCell collapsing>Sent</TableHeaderCell>
                      <TableHeaderCell collapsing>Delivered</TableHeaderCell>
                      <TableHeaderCell collapsing>Opened</TableHeaderCell>
                      <TableHeaderCell collapsing>Bounced</TableHeaderCell>
                    </>
                  ) : null}
                  <TableHeaderCell></TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {messages.map((message) => {
                  const messageLog = message.emailLogs?.[0];
                  return (
                    <TableRow key={message._id}>
                      <TableCell>{message.subject}</TableCell>
                      {canListUsers ? (
                        <TableCell collapsing>
                          {message.ownerUser ? (
                            <UserChip user={message.ownerUser} />
                          ) : null}
                        </TableCell>
                      ) : null}
                      <TableCell collapsing>{message.replyTo}</TableCell>
                      <TableCell collapsing>
                        {formatDate(message.createdAt)}
                      </TableCell>
                      {allMessagesWithLogs.length > 0 ? (
                        <>
                          <TableCell>
                            {messageLog?.sent ? (
                              <Icon icon={CheckIcon} color="green" />
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {messageLog?.events?.delivered ? (
                              <Icon icon={CheckIcon} color="green" />
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {messageLog?.events?.open ? (
                              <Icon icon={CheckIcon} color="green" />
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {messageLog?.events?.bounced ? (
                              <Icon icon={XIcon} color="red" />
                            ) : null}
                          </TableCell>
                        </>
                      ) : null}

                      <TableCell collapsing>
                        <Button onClick={() => setSelectedMessage(message)}>
                          View message
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <Message>
              <p>No messages have been sent to this company yet.</p>
            </Message>
          )}
        </div>
      </InfoBoxWidget>
    </>
  );
};

export default CompanyMessages;
